import React, {useCallback, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Add} from "@mui/icons-material";
import {Box, SvgIcon, Tooltip} from "@mui/material";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState,} from "../../../../../../../../packages/data-grid";
import {ProductFlatRatesListDataGridColumnNames} from "../../../../../../../../core/constants/table/data-grid-column-keys";
import Button from "../../../../../../../components/base/button/button";
import SearchInput from "../../../../../../../components/base/input/search_input";
import PartnerDataGrid from "../../../../../../../containers/data-grid";
import UpsertPartRateDialog from "../../../../../../../components/dialogs/upsert-part-rate-dialog";
import Api from "../../../../../../../../core/services/api/api";
import useSearch from "../../../../../../../hooks/use-search";
import useAccessLevels from "../../../../../../../hooks/use-access-levels";
import {useAppDispatch} from "../../../../../../../redux";
import {setReduxDialogState} from "../../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../../core/models/static/routes/redux-store-routes";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {ReactComponent as RemoveIcon} from "../../../../../../../../assets/images/remove.svg";
import ProductInformationPopover from "../../../../../../../components/app-specific/draggable-popover/product-information-popover";


const initUpsertDialogState = {open: false, data: null};

const dataGridSaveConfig = {
    name: 'ProductFlatRatesListViewDataGrid',
    version: 2,
};

/**
 * Product flat rates (part rates) list panel: search, grid, add dialog, and permission-gated remove.
 */
const ProductFlatRatesSection = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.shipping-fees.product-flat-rates'});
    const operatorAccessLevels = useAccessLevels();
    const dispatch = useAppDispatch();
    const [data, setData] = useState([]);
    const [upsertDialogState, setUpsertDialogState] = useState(initUpsertDialogState);
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<DataGridController>}*/
    const dataGridControllerRef = useRef();

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    title: t('columns.partNumber'),
                    name: ProductFlatRatesListDataGridColumnNames.partNumber,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 220,
                },
                {
                    title: t('columns.rate'),
                    name: ProductFlatRatesListDataGridColumnNames.rate,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.money,
                    sortable: false,
                },
                ...(operatorAccessLevels.packages.sales.managePartRates ? [{
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    name: ProductFlatRatesListDataGridColumnNames.remove,
                    pinned: true,
                    resizable: false,
                    visibilityToggleable: false,
                    sortable: false,
                    width: 0,
                }] : []),
            ],
        [operatorAccessLevels.packages.sales.managePartRates, t]);

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialSearchDataSettings = useMemo(() => ({
        filters: {keyword: ''},
    }), []);

    /**
     * Fetches part rates from the server.
     *
     * @type {(function(UseSearchFunctionArg): Promise<void>)}
     */
    const fetchEntries = useCallback(async (args) => {
        const response = await Api.listPartRates({
            keyword: args.filters?.keyword ?? '',
        }, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.aborted)
            return;
        if (response?.resultFlag) {
            setData(response.data ?? []);
        }
    }, [isMounted]);

    /**
     * Searches part rates based on keyword filter.
     *
     * @type {UseSearchFunction}
     */
    const searchEntries = useCallback(async (result) => {
        await fetchEntries(result);
    }, [fetchEntries]);

    const searchData = useSearch(searchEntries, initialSearchDataSettings);

    /**
     * Updates keyword filter and triggers search.
     *
     * @type {function(string): void}
     */
    const searchWithKeywords = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword?.trim(),
        });
    }, [searchData]);

    /**
     * Opens the upsert dialog for creating a new product flat rate.
     */
    const onAddPartRateClicked = useCallback(() => {
        setUpsertDialogState({open: true, data: null});
    }, []);

    /**
     * Removes a part rate by its original part id.
     *
     * @type {function(number): Promise<void>}
     */
    const removePartRate = useCallback(async (originalPartId) => {
        searchData.setLoading(true);
        const response = await Api.removePartRate({partOriginalIds: [originalPartId]});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.filter(e => e.originalPartId !== originalPartId));
        }
        searchData.setLoading(false);
    }, [isMounted, searchData]);

    /**
     * Opens the confirmation dialog before removing a part rate.
     *
     * @type {function(number): void}
     */
    const getRemoveConfirmation = useCallback((originalPartId) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
            open: true,
            title: t('removeConfirmationTitle'),
            description: t('removeConfirmationDescription'),
            confirmButtonText: t('removeConfirm'),
            cancelButtonText: t('cancel'),
            maxWidth: 'sm',
            fullWidth: false,
            onClose: async (confirmed) => {
                if (confirmed)
                    await removePartRate(originalPartId);
                return true;
            },
        }));
    }, [dispatch, removePartRate, t]);

    /**
     * Adds or updates a part rate in the local list after a successful upsert.
     *
     * @type {function(*): void}
     */
    const onPartRateUpserted = useCallback((partRate) => {
        setData(prevState => {
            const foundIndex = prevState.findIndex(e => e.originalPartId === partRate.originalPartId);
            if (foundIndex === -1)
                return [partRate, ...prevState];
            return prevState.map(e => e.originalPartId === partRate.originalPartId ? {...e, ...partRate} : e);
        });
    }, []);

    /**
     * Builds data grid row entities from the fetched data.
     */
    const dataGridRows = useMemo(() => data?.map(partRate => ({
        key: partRate.originalPartId ?? partRate.id,
        data: partRate,
        cells: {
            [ProductFlatRatesListDataGridColumnNames.partNumber]:
                <ProductInformationPopover partNo={partRate.partNumber}>
                    {partRate.partNumber ?? ''}
                </ProductInformationPopover>,
            [ProductFlatRatesListDataGridColumnNames.rate]: partRate.amount,
            ...(operatorAccessLevels.packages.sales.managePartRates && {
                [ProductFlatRatesListDataGridColumnNames.remove]:
                    <Tooltip title={t('removeTooltip')}>
                        <IconButton
                            onPressed={() => getRemoveConfirmation(partRate.originalPartId)}
                            color={'paper.error'}
                            className={'remove-icon'}
                            disabled={searchData.loading}
                        >
                            <SvgIcon component={RemoveIcon} className='icon normal'/>
                        </IconButton>
                    </Tooltip>,
            }),
        },
    })), [data, getRemoveConfirmation, operatorAccessLevels.packages.sales.managePartRates, searchData.loading, t]);

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 4}}>
                {
                    operatorAccessLevels.packages.sales.managePartRates &&
                    <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                        <Button
                            className={'d-flex align-items-center justify-content-center'}
                            variant={'outlined'}
                            color={'primary'}
                            onClick={onAddPartRateClicked}
                        >
                            <Add className='icon small mr-2'/>
                            <p className={'button-label'}>{t('addButton')}</p>
                        </Button>
                    </Box>
                }
                <SearchInput
                    loading={searchData.loading}
                    initialValue={searchData.filters?.keyword}
                    autoSearch
                    immediateSearch
                    search={searchWithKeywords}
                    label={t('searchLabel')}
                />
                <PartnerDataGrid
                    ref={dataGridControllerRef}
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={dataGridRows}
                    hidePagination
                    hidePageSize
                    loading={searchData.loading}
                    state={{
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                    }}
                />
            </Box>
            <UpsertPartRateDialog
                open={upsertDialogState.open}
                close={() => setUpsertDialogState(initUpsertDialogState)}
                data={upsertDialogState.data}
                onSuccess={onPartRateUpserted}
            />
        </>
    );
};

export default ProductFlatRatesSection;
